import type { VistaCard } from "../contratos";
import { lazy, Suspense, useCallback, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { useConsulta } from "../api/consulta";
import {
  imagemDoCard,
  textoDaAnalise,
  textoDoCard,
  verVista,
  verVistaDaAnalise,
  type Estado,
} from "../api/prisma";
import { useTitulo } from "../app/titulo";
import type { NomeBloco } from "../card/Card";
import { Doca } from "../mesa/Doca";
import { Documento } from "../mesa/Documento";
import type { FaseInspetor } from "../mesa/Inspetor";
import { Mesa } from "../mesa/Mesa";
import { envioDe } from "../mesa/memoria";
import { Rastro } from "../mesa/Rastro";
import estilos from "../mesa/mesa.module.css";

// Inspetor e caixa de chat carregam sob demanda: a primeira carga do card cabe no orçamento do
// ADR 0010 e o card abre primeiro.
const InspetorTardio = lazy(() => import("../mesa/Inspetor"));

function Inspetor(props: FaseInspetor) {
  return (
    <Suspense fallback={null}>
      <InspetorTardio {...props} />
    </Suspense>
  );
}

function titulo(p: FaseInspetor): string {
  if (p.fase === "checando") return "Registro ao vivo";
  if (p.fase === "resultado" && p.escolhido) return `Evidência · ${p.escolhido}`;
  return "Ficha da análise";
}

/** A mensagem enviada, como bolha à direita; existe só na memória da aba de quem enviou. */
function Fio(props: { token?: string | undefined; children: ReactNode }) {
  const enviado = props.token ? envioDe(props.token) : undefined;
  return (
    <>
      {enviado && (
        <div className={estilos.bolha}>
          <p>{enviado.texto}</p>
          {enviado.anexo && <p>📎 {enviado.anexo}</p>}
          <small>você · nesta aba</small>
        </div>
      )}
      <p className={estilos.agente}>
        <span className={estilos.avatar} aria-hidden="true">
          △
        </span>
        Prisma
      </p>
      {props.children}
    </>
  );
}

function Resultado(props: {
  vista: VistaCard;
  token?: string | undefined;
  atual?: string | undefined;
  textoUrl: string;
  imagemUrl: string | null;
  recarregar: () => void;
  doca: boolean;
}) {
  const [escolhido, setEscolhido] = useState<NomeBloco | null>(null);
  const fechar = useCallback(() => {
    setEscolhido(null);
  }, []);
  const fase: FaseInspetor = { fase: "resultado", vista: props.vista, escolhido, aoFechar: fechar };
  return (
    <Mesa
      migalha={props.vista.alegacao}
      atual={props.atual}
      doca={props.doca && <Doca />}
      inspetor={(naFolha) => <Inspetor {...fase} naFolha={naFolha} />}
      inspetorAberto={escolhido !== null}
      tituloInspetor={titulo(fase)}
      aoFecharInspetor={fechar}
    >
      <Fio token={props.token}>
        <Rastro estado="concluida" />
        <Documento
          vista={props.vista}
          textoUrl={props.textoUrl}
          imagemUrl={props.imagemUrl}
          recarregar={props.recarregar}
          escolhido={escolhido}
          aoEscolher={setEscolhido}
        />
      </Fio>
    </Mesa>
  );
}

function Checando(props: {
  estado: Estado;
  token?: string | undefined;
  atual?: string | undefined;
}) {
  const { estado, token, atual } = props;
  const navegar = useNavigate();
  const fase: FaseInspetor = { fase: "checando", estado };
  return (
    <Mesa
      migalha="Checando…"
      atual={atual}
      doca={
        <Doca
          rodando
          aoParar={() => {
            void navegar("/");
          }}
        />
      }
      inspetor={(naFolha) => <Inspetor {...fase} naFolha={naFolha} />}
      inspetorAberto={false}
      tituloInspetor={titulo(fase)}
      aoFecharInspetor={() => undefined}
    >
      <h1 tabIndex={-1} className="visualmente-oculto">
        Checando a afirmação
      </h1>
      <Fio token={token}>
        <Rastro estado={estado} />
        <div className={estilos.esqueleto} aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
        <p className={estilos.rotulo}>
          O resultado aparece aqui quando ficar pronto. Guarde o endereço desta página: só quem tem
          o link vê o card.
        </p>
      </Fio>
    </Mesa>
  );
}

/** O card pelo link privado (`/c/{token}`) ou compartilhado (`/card/{id}`), sobre a mesa. */
function CardPagina({ chave, privado }: { chave: string; privado: boolean }) {
  const [busca] = useSearchParams();
  const versao = busca.get("versao");
  // After a dispute, the new version arrives when the reanalysis ends: keep polling until it
  // shows up (`awaitedVersion` is the version expected; 0 means none).
  const [awaitedVersion, setAwaitedVersion] = useState(0);
  // Enquanto a análise anda, consulta de novo no intervalo que o servidor sugere.
  const consulta = useConsulta(
    `${chave}?${versao ?? ""}`,
    () => verVista(chave, versao),
    (r) =>
      r.estado !== "concluida" && r.estado !== "falhou"
        ? r.atualizar_em * 1000
        : (r.vista?.versao ?? awaitedVersion) < awaitedVersion && 5000,
  );
  const dados = consulta.dados;
  useTitulo(dados?.vista ? dados.vista.alegacao : dados ? "Checando" : "Card");
  const token = privado ? chave : undefined;
  const atual = privado ? `/c/${chave}` : undefined;

  if (consulta.erro) throw consulta.erro;
  if (!dados) {
    return (
      <p role="status" aria-live="polite" style={{ padding: "0 1rem" }}>
        Abrindo o card…
      </p>
    );
  }
  if (dados.vista) {
    return (
      <Resultado
        key={chave}
        vista={dados.vista}
        token={token}
        atual={atual}
        textoUrl={textoDoCard(chave)}
        imagemUrl={imagemDoCard(chave)}
        recarregar={() => {
          setAwaitedVersion((dados.vista?.versao ?? 0) + 1);
          consulta.recarregar();
        }}
        doca
      />
    );
  }
  if (dados.estado === "falhou") {
    return (
      <Mesa
        migalha="Não terminou"
        atual={atual}
        doca={<Doca />}
        inspetor={(naFolha) => <Inspetor fase="vazia" naFolha={naFolha} />}
        inspetorAberto={false}
        tituloInspetor="Como o Prisma decide"
        aoFecharInspetor={() => undefined}
      >
        <h1 tabIndex={-1}>Não conseguimos terminar esta análise</h1>
        <p role="status">
          Algo deu errado do nosso lado ao consultar as fontes ou os modelos. Nada foi publicado.
          Tente enviar de novo daqui a alguns minutos.
        </p>
        <p>
          <Link to="/">Começar uma nova análise</Link>
        </p>
      </Mesa>
    );
  }
  return <Checando estado={dados.estado} token={token} atual={atual} />;
}

export function CardPrivado() {
  const { token = "" } = useParams();
  return <CardPagina key={token} chave={token} privado />;
}

export function CardPublico() {
  const { id = "" } = useParams();
  return <CardPagina key={id} chave={id} privado={false} />;
}

/**
 * Uma análise linkada por divergência entre composições de modelos (`/analise/{id}`, ADRs 0012
 * e 0015): só o dossiê, sem contestar nem compartilhar.
 */
export function AnalisePagina() {
  const { id = "" } = useParams();
  const [busca] = useSearchParams();
  const versao = busca.get("versao");
  const consulta = useConsulta(
    `analise:${id}?${versao ?? ""}`,
    () => verVistaDaAnalise(id, versao),
    () => false,
  );
  const vista = consulta.dados?.vista;
  useTitulo(vista ? vista.alegacao : "Análise");
  if (consulta.erro) throw consulta.erro;
  if (!vista) {
    return (
      <p role="status" aria-live="polite" style={{ padding: "0 1rem" }}>
        Abrindo a análise…
      </p>
    );
  }
  return (
    <Resultado
      key={id}
      vista={vista}
      textoUrl={textoDaAnalise(id)}
      imagemUrl={null}
      recarregar={consulta.recarregar}
      doca={false}
    />
  );
}
